export const COLORS = {
  primary: '#0F8277',
  primaryDark: '#067857',
} as const;

export interface RegulationDatabaseLink {
  name: string;
  url: string;
}

/**
 * Builds regulation database links using a single law for every regulation
 * (all regulations in the template share the template's first law).
 */
export const buildSingleLawRegulationLinks = (
  law: { id: number } | undefined,
  regulations: { id: number; name: string }[],
  baseUrl: string
): RegulationDatabaseLink[] => {
  if (!law || !baseUrl) return [];

  return regulations.map((regulation) => ({
    name: regulation.name,
    url: `${baseUrl}/${law.id}/${regulation.id}`,
  }));
};

/**
 * Builds regulation database links by matching each regulation to its own
 * law via law_id, falling back to the first law when a regulation has none.
 */
export const buildMappedRegulationLinks = (
  laws: { id: number }[],
  regulations: { id: number; name: string; law_id?: number | null }[],
  baseUrl: string
): RegulationDatabaseLink[] => {
  if (!baseUrl || regulations.length === 0) return [];

  const lawMap = new Map(laws.map((law) => [law.id, law]));
  const links: RegulationDatabaseLink[] = [];

  regulations.forEach((regulation) => {
    const law = regulation.law_id ? lawMap.get(regulation.law_id) : laws[0];

    if (law) {
      links.push({
        name: regulation.name,
        url: `${baseUrl}/${law.id}/${regulation.id}`,
      });
    }
  });

  return links;
};

export const TAG_CONFIG = {
  law: {
    chipClass: 'tag-chip-law',
    icon: 'mdi-book-open-variant',
  },
  business: {
    chipClass: 'tag-chip-standard',
    icon: 'mdi-ruler',
  },
  region: {
    chipClass: 'tag-chip-country',
    icon: 'mdi-map-marker',
  },
  classification: {
    chipClass: 'tag-chip-classification',
    icon: 'mdi-format-list-bulleted',
  },
} as const;

export interface TagItem {
  key: string;
  name: string;
  type: string;
  chipClass: string;
  icon: string;
}

interface TagSource {
  labels?: Array<{ id: number; name: string; type: string }>;
}

export const buildTagItems = (template: TagSource): TagItem[] => {
  const tags: TagItem[] = [];

  (template.labels || []).forEach((label) => {
    const config = TAG_CONFIG[label.type as keyof typeof TAG_CONFIG];
    if (config) {
      tags.push({
        key: `${label.type}-${label.id}`,
        name: label.name,
        type: label.type,
        chipClass: config.chipClass,
        icon: config.icon,
      });
    }
  });

  return tags;
};

export const parseLawName = (name: string): string => {
  const parts = name.split(';').map((part) => part.trim());
  const abbreviation = parts[1] || parts[0];
  return abbreviation === '-' ? parts[0] : abbreviation;
};

// dbt_type の分類定数
export const THRESHOLD_CONSIDER_TYPES: readonly string[] = [
  'thresholds',
  'thresholds_over',
  'cas_material_thresholds_without_section15',
];

export const THRESHOLD_NOT_CONSIDER_TYPES: readonly string[] = [
  'cas_material_without_section15',
  'cas_material_without_section15_not_even_one',
  'cas_material_excluding_section15_false',
];

const CAS_MATERIAL_TYPES: readonly string[] = [
  ...THRESHOLD_CONSIDER_TYPES,
  ...THRESHOLD_NOT_CONSIDER_TYPES,
];

const CAS_RELATED_TYPES = ['cas'];
const MATERIAL_RELATED_TYPES = ['material'];

// すべてのスキップ対象タイプ
const SKIP_TYPES = [
  ...CAS_RELATED_TYPES,
  ...MATERIAL_RELATED_TYPES,
  ...CAS_MATERIAL_TYPES,
];

/**
 * dbt_types に閾値「考慮する」タイプが含まれるか判定
 */
export const checkHasThreshold = (dbtTypes: string[]): boolean => {
  return dbtTypes.some((type) => THRESHOLD_CONSIDER_TYPES.includes(type));
};

/**
 * dbt_types からUI表示用のラベル配列を生成する
 * @param dbtTypes - dbt_type の配列
 * @param labelMap - dbt_type → 翻訳済みラベルのマップ（cas, material, regulation 等）
 */
export const buildDbtTypeLabels = (
  dbtTypes: string[],
  labelMap: Record<string, string>
): string[] => {
  const labels: string[] = [];

  const impliesCasMaterial = dbtTypes.some((type) =>
    CAS_MATERIAL_TYPES.includes(type)
  );

  // CAS番号
  if (
    impliesCasMaterial ||
    dbtTypes.some((type) => CAS_RELATED_TYPES.includes(type))
  ) {
    const casLabel = labelMap.cas;
    if (casLabel && !labels.includes(casLabel)) {
      labels.push(casLabel);
    }
  }

  // 物質名
  if (
    impliesCasMaterial ||
    dbtTypes.some((type) => MATERIAL_RELATED_TYPES.includes(type))
  ) {
    const materialLabel = labelMap.material;
    if (materialLabel && !labels.includes(materialLabel)) {
      labels.push(materialLabel);
    }
  }

  // その他のタイプ（regulation等）
  dbtTypes.forEach((type) => {
    if (SKIP_TYPES.includes(type)) {
      return;
    }
    const label = labelMap[type];
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  });

  return labels;
};
